(function () {
  'use strict';

  function cfgLog() { if (window.cfgDebug) { try { console.log.apply(console, arguments); } catch (e) {} } }

  // Same pairing rule as linked-removal.js and vs-cart-ordered-keys.liquid.
  var UPGRADE_HANDLE = 'personalization-upgrade';

  // One sync at a time per _Linked Group; the triggering input is also disabled
  // while a sync is in flight (guards double Enter / rapid re-fires).
  var inFlightGroups = new Set();

  // Once a cart-page sync commits to navigating, ignore every further change
  // event — sequenced fetches racing an in-flight navigation produce partial
  // cart states.
  var navigationPending = false;

  function isCartPage() {
    var pageType = window.themeVariables?.settings?.pageType;
    return pageType === 'cart' || window.location.pathname === '/cart';
  }

  function routeRoot() {
    return window.Shopify?.routes?.root ?? '/';
  }

  function cssEscape(value) {
    return window.CSS?.escape ? CSS.escape(value) : value;
  }

  // Child = upgrade-handle line-item sharing the parent's _Linked Group.
  // The same cart line renders twice on /cart (table + drawer), so dedupe by
  // line key — one /cart/change.js per LINE, not per DOM copy.
  function getChildLineItems(group, parentKey) {
    var selector = 'line-item[data-linked-group="' + cssEscape(group) + '"]';
    var seenKeys = new Set();
    return Array.prototype.slice.call(document.querySelectorAll(selector)).filter(function (el) {
      var key = el.dataset.lineKey;
      if (el.dataset.productHandle !== UPGRADE_HANDLE || key === parentKey) return false;
      if (!key || seenKeys.has(key)) return false;
      seenKeys.add(key);
      return true;
    });
  }

  // Mirror the theme's busy UI (pill-loader aria-busy, cf. linked-removal.js).
  function setProcessingState(lineItems, isProcessing) {
    lineItems.forEach(function (item) {
      var loader = item.querySelector('pill-loader');
      if (!loader) return;
      if (isProcessing) loader.setAttribute('aria-busy', 'true');
      else loader.removeAttribute('aria-busy');
    });
  }

  // Sequential /cart/change.js by LINE KEY (never variant id — the same upgrade
  // variant can appear on multiple lines). `sections` only on the final call so
  // the drawer re-renders once, from the final cart state (no stale innerHTML).
  function changeLine(lineKey, quantity, sections) {
    var body = { id: lineKey, quantity: quantity };
    if (sections?.length) body.sections = sections;
    return fetch(routeRoot() + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (response) {
      if (!response.ok) throw new Error('/cart/change.js HTTP ' + response.status);
      return response.json();
    });
  }

  async function syncGroupQuantity(input, parentLineItem, group, children, quantity) {
    var allLineItems = [parentLineItem].concat(children);

    inFlightGroups.add(group);
    input.disabled = true;
    setProcessingState(allLineItems, true);

    try {
      // 1. Parent first, no sections (intermediate state must not render).
      await changeLine(parentLineItem.dataset.lineKey, quantity, null);

      // 2. Children sequentially; bundle drawer sections only on the LAST call.
      var bundledSections = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: bundledSections }
      }));

      var finalCart = null;
      for (var i = 0; i < children.length; i++) {
        var isLast = i === children.length - 1;
        var sections = isLast && !isCartPage() ? bundledSections : null;
        finalCart = await changeLine(children[i].dataset.lineKey, quantity, sections);
      }

      cfgLog('[vs-quantity-sync] Group', group, 'synced to quantity', quantity);

      if (isCartPage()) {
        // The theme's own cart-page path is a full navigation; mirror it.
        navigationPending = true;
        window.location.href = routeRoot() + 'cart';
      } else {
        // Single re-render from the final response's bundled sections. Prices
        // come from the section HTML (.money spans), never written by JS.
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'line-item:change', cart: finalCart }
        }));
      }
    } catch (err) {
      console.error('[vs-quantity-sync] Failed:', err);
      if (isCartPage()) {
        // Cart may be partially updated — reload to show the truth.
        navigationPending = true;
        window.location.href = routeRoot() + 'cart';
      } else {
        // Full drawer refresh restores a consistent view.
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        setProcessingState(allLineItems, false);
      }
    } finally {
      inFlightGroups.delete(group);
      input.disabled = false;
    }
  }

  // Document CAPTURE-phase change listener: runs before QuantityInput's own
  // handlers and before <line-item-quantity>'s bubble-phase handler, on both the
  // cart page (input lives in a <td> outside <line-item>) and the drawer.
  document.addEventListener('change', function (event) {
    if (navigationPending) { event.stopImmediatePropagation(); return; }

    var input = event.target;
    if (!input?.matches?.('input[data-line-key]')) return;

    var lineKey = input.getAttribute('data-line-key');
    if (!lineKey) return;

    var lineItem = document.querySelector('line-item[data-line-key="' + cssEscape(lineKey) + '"]');
    if (!lineItem) return;

    var group = lineItem.dataset.linkedGroup;
    if (!group) return;

    // Upgrade lines themselves (orphans keep an input) sync nothing.
    if (lineItem.dataset.productHandle === UPGRADE_HANDLE) return;

    var children = getChildLineItems(group, lineKey);
    if (children.length === 0) return; // parent without a child in cart: native path

    // This is a parent with a linked upgrade: take the change over entirely.
    event.stopImmediatePropagation();

    if (inFlightGroups.has(group)) {
      cfgLog('[vs-quantity-sync] Group', group, 'already in flight, ignoring');
      return;
    }

    // Normalize like QuantityInput does (its own change handler is suppressed
    // by stopImmediatePropagation above). Value is already max-clamped on input.
    var quantity = parseInt(input.value, 10);
    if (isNaN(quantity) || quantity < 0) quantity = 1;
    var max = parseInt(input.getAttribute('max'), 10);
    if (!isNaN(max)) quantity = Math.min(quantity, max);
    input.value = quantity;

    syncGroupQuantity(input, lineItem, group, children, quantity);
  }, true);

  cfgLog('[vs-quantity-sync] Module loaded, capture-phase change listener attached');
})();
